/**
 * ChatInput.tsx：底部输入区 —— Enter 发送 / Shift+Enter 换行 / 停止。
 * 数据流：本地 text → submit() 回调 onSend（App.handleSend）并清空输入框；
 *   loading 时按钮变「停止」走 onStop（AbortController 中断 SSE）。
 * allowEmptySend=true 允许空文本发送（只发附件/工作区文件的场景，由 App 按附件数计算）。
 */
// ============================================================================
// ChatInput.tsx：底部输入区 —— Enter 发送 / Shift+Enter 换行 / 停止
// ============================================================================

import { useState, type KeyboardEvent } from "react";
import { Button, Input } from "antd";
import { PauseCircleOutlined, SendOutlined } from "@ant-design/icons";

type Props = {
  loading: boolean;
  onSend: (text: string) => void;
  onStop: () => void;
  allowEmptySend?: boolean;
};

export function ChatInput({
  loading,
  onSend,
  onStop,
  allowEmptySend = false,
}: Props) {
  const [text, setText] = useState("");

  const submit = () => {
    const t = text.trim();
    // 空文本且不允许空发、或请求进行中：直接忽略（按钮 disabled 是第二道闸）
    if ((!t && !allowEmptySend) || loading) return;
    onSend(t);
    setText("");
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <div className="composer ant-composer">
      <Input.TextArea
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder="输入消息… Enter 发送，Shift+Enter 换行"
        disabled={loading}
        autoSize={{ minRows: 3, maxRows: 8 }}
        className="ant-composer-input"
      />
      <div className="composer-actions">
        {/* loading 中只能「停止」；否则显示「发送」，禁用条件与 submit 守卫一致 */}
        {loading ? (
          <Button danger icon={<PauseCircleOutlined />} onClick={onStop}>
            停止
          </Button>
        ) : (
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={submit}
            disabled={!text.trim() && !allowEmptySend}
          >
            发送
          </Button>
        )}
      </div>
    </div>
  );
}
